import { Booking } from "../types/booking";
import { generateAndSavePackingSlip } from "../utils/packingSlip";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage } from "../lib/posthog";
import { message } from "antd";
import { textNeedsCjkFont } from "../utils/fontPreloader";

// Define additional interfaces for better type checking for better type checking
interface SupplierInfo {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  country?: string;
  province?: string;
  address?: string;
  postal_code?: string;
  [key: string]: unknown;
}

interface CompanyInfo {
  id?: number;
  name: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  eori_number?: string;
  coc_number?: string;
  [key: string]: unknown;
}

interface TransportTypeInfo {
  name: string;
  abbreviation: string;
  [key: string]: unknown;
}

// Product info interface that matches what we need
interface ProductItem {
  id: number;
  name: string;
  pieces?: number | null;
  cartons?: number | null;
  gross_weight: number;
  cbm: number;
  ean: any; // Using any to avoid type conflicts
  hs_code?: string | null;
  [key: string]: unknown;
}

// Interface matching packingSlip.ts expected format
interface FormattedBooking {
  id: number;
  reference: string;
  transport_type: {
    name: string;
    abbreviation: string;
  };
  created_at: string;
  incoterm?: string;
  containerOrigin?: string;
  supplier: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
    postal_code?: string;
  };
  company: {
    id: number;
    name: string;
    address: string;
    city: string;
    country: string;
    postal_code?: string;
    eori_number: string;
    coc_number: string;
    street_name: string;
    house_number: string;
  };
  products: {
    id: number;
    name: string;
    pieces?: number | null;
    cartons?: number | null;
    gross_weight: number;
    cbm: number;
    ean: number | null;
    hs_code?: string | null;
    net_weight: number;
  }[];
}

// Our own DeliveryInfo interface
interface DeliveryInfoType {
  street_name?: string;
  house_number?: string;
  [key: string]: unknown;
}

/** Build FormattedBooking for packing slip generation (shared by generateAndDownloadPackingSlip and ensurePackingSlipForBooking) */
function buildFormattedBooking(booking: Booking): FormattedBooking {
  const supplierInfo = Array.isArray(booking.supplier_info) ? booking.supplier_info[0] : booking.supplier_info || {};
  const supplier = supplierInfo as SupplierInfo;
  const companyInfo = Array.isArray(booking.company_info) ? booking.company_info[0] : booking.company_info || {};
  const company = companyInfo as CompanyInfo;
  const transportTypeInfo = Array.isArray(booking.transport_type_info) ? booking.transport_type_info[0] : booking.transport_type_info || {};
  const transportType = transportTypeInfo as TransportTypeInfo;
  let incotermName: string | undefined;
  if (booking.booking_incoterm_info) {
    const incotermInfo = Array.isArray(booking.booking_incoterm_info) ? booking.booking_incoterm_info[0] : booking.booking_incoterm_info;
    incotermName = (incotermInfo as any)?.name;
  }
  let containerOrigin: string | undefined;
  if (booking.container_info) {
    const containerInfo = Array.isArray(booking.container_info) ? booking.container_info[0] : booking.container_info;
    if ((containerInfo as any)?.container_origin_port) {
      const originPort = Array.isArray((containerInfo as any).container_origin_port) ? (containerInfo as any).container_origin_port[0] : (containerInfo as any).container_origin_port;
      containerOrigin = originPort?.name;
    } else if ((containerInfo as any)?.origin_port_info) {
      const originPort = Array.isArray((containerInfo as any).origin_port_info) ? (containerInfo as any).origin_port_info[0] : (containerInfo as any).origin_port_info;
      containerOrigin = originPort?.name;
    }
  }
  let productsData: any[] = [];
  if (Array.isArray(booking.product_info)) {
    productsData = booking.product_info.length > 0 && Array.isArray(booking.product_info[0]) ? booking.product_info[0] : booking.product_info;
  }
  const productArray = productsData as ProductItem[];
  let deliveryInfo: DeliveryInfoType = { street_name: "", house_number: "" };
  if (booking.booking_delivery_info) {
    const d = Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length > 0 ? booking.booking_delivery_info[0] : booking.booking_delivery_info;
    deliveryInfo = { street_name: (d as any)?.street_name || "", house_number: (d as any)?.house_number || "" };
  }
  return {
      id: booking.id,
      reference: booking.reference || `EFF${booking.id}`,
      transport_type: {
        name: transportType.name || "Unknown",
        abbreviation: transportType.abbreviation || ""
      },
      created_at: booking.created_at,
      incoterm: incotermName,
      containerOrigin: containerOrigin || undefined,
      supplier: {
        name: supplier.name || "Unknown",
        email: supplier.email || "",
        phone: supplier.phone || "",
        country: supplier.country || "",
        city: supplier.province || "", // Use province as city
        address: supplier.address || "",
        postal_code: supplier.postal_code
      },
      company: {
        id: company.id || 0,
        name: company.name || "Unknown",
        address: company.address || "",
        city: company.city || "",
        country: company.country || "",
        postal_code: company.postal_code,
        eori_number: company.eori_number || "",
        coc_number: company.coc_number || "",
        street_name: deliveryInfo.street_name || "",
        house_number: deliveryInfo.house_number || ""
      },
      products: productArray.map(p => ({
        id: p.id || 0,
        name: p.name || "Unknown",
        pieces: p.pieces,
        cartons: p.cartons,
        gross_weight: p.gross_weight || 0,
        cbm: p.cbm || 0, 
        ean: p.ean || null, // Convert to null for the expected type
        hs_code: p.hs_code || null,
        net_weight: p.gross_weight ? p.gross_weight * 0.9 : 0
      }))
  };
}

/**
 * Generate and download a packing slip for a booking.
 * Uses client-side generation only (React PDF). Edge Function is not used.
 */
export const generateAndDownloadPackingSlip = async (booking: Booking): Promise<boolean> => {
  const startTime = performance.now();
  console.log("🚀 [PackingSlip] Starting generation for booking:", booking.id);

  try {
    message.loading("Generating packing slip (this may take a moment)...", 0);
    await new Promise(resolve => setTimeout(resolve, 0));

    const formattedBooking = buildFormattedBooking(booking);
    const filePath = await generateAndSavePackingSlip(formattedBooking);
    message.destroy();

    if (!filePath) {
      message.error("Failed to generate packing slip");
      return false;
    }

    const { data, error } = await supabase.storage
      .from("packingslips")
      .createSignedUrl(filePath, 60 * 60);

    if (error || !data?.signedUrl) {
      message.error("Failed to generate download link");
      return false;
    }

    window.open(data.signedUrl, "_blank");
    message.success("Packing slip opened in new tab");
    console.log(`✅ [PackingSlip] Client generation: ${(performance.now() - startTime).toFixed(0)}ms`);
    trackFeatureUsage("admin_booking_packingslip_download");
    return true;
  } catch (error) {
    console.error("Error generating packing slip:", error);
    message.destroy();
    message.error("Failed to generate packing slip");
    return false;
  }
};

function getPackingSlipStoragePath(booking: Booking): string {
  const transportTypeInfo = Array.isArray(booking.transport_type_info)
    ? booking.transport_type_info[0]
    : booking.transport_type_info || {};
  const abbreviation = (transportTypeInfo as TransportTypeInfo).abbreviation || "";
  return `${booking.company_id}/${booking.id}/packing_slip_${booking.id}_${abbreviation}.pdf`;
}

async function packingSlipExistsInStorage(filePath: string): Promise<boolean> {
  const lastSlash = filePath.lastIndexOf("/");
  const folder = lastSlash >= 0 ? filePath.slice(0, lastSlash) : "";
  const fileName = lastSlash >= 0 ? filePath.slice(lastSlash + 1) : filePath;
  const { data, error } = await supabase.storage.from("packingslips").list(folder, {
    limit: 50,
    search: fileName,
  });
  if (error || !data) return false;
  return data.some((entry) => entry.name === fileName);
}

export async function getExistingPackingSlipStoragePath(
  booking: Booking
): Promise<string | null> {
  const filePath = getPackingSlipStoragePath(booking);
  const exists = await packingSlipExistsInStorage(filePath);
  return exists ? filePath : null;
}

export function bookingNeedsCjkPackingSlipFont(booking: Booking): boolean {
  const formatted = buildFormattedBooking(booking);
  return textNeedsCjkFont([
    formatted.supplier.name,
    formatted.supplier.address,
    formatted.supplier.city,
    formatted.supplier.country,
    formatted.company.name,
    formatted.company.address,
    formatted.company.city,
    formatted.company.country,
    formatted.incoterm,
    formatted.containerOrigin,
    ...formatted.products.flatMap((p) => [p.name, p.ean, p.hs_code]),
  ]);
}

/**
 * Ensure a packing slip exists for the booking (generate and save if not). Returns the storage path, or null on failure.
 * Uses client-side generation (React PDF) so output matches manual downloads and table text stays readable.
 * For use when forwarding to e.g. LeanCustoms.
 */
export const ensurePackingSlipForBooking = async (booking: Booking): Promise<string | null> => {
  try {
    const existing = await getExistingPackingSlipStoragePath(booking);
    if (existing) return existing;

    const formattedBooking = buildFormattedBooking(booking);
    return await generateAndSavePackingSlip(formattedBooking);
  } catch (err) {
    console.error("Error ensuring packing slip:", err);
    return null;
  }
};

/**
 * Get a previously generated packing slip
 */
export const getPackingSlip = async (booking: Booking): Promise<string | null> => {
  try {
    // Get transport type safely
    const transportType = Array.isArray(booking.transport_type_info) 
      ? booking.transport_type_info[0] 
      : booking.transport_type_info;
    
    const filePath = `${booking.company_id}/${booking.id}/packing_slip_${booking.id}_${
      transportType?.abbreviation || ""
    }.pdf`;
    
    // Check if file exists
    const { data, error } = await supabase.storage
      .from("packingslips")
      .createSignedUrl(filePath, 60 * 60); // 1 hour expiry
    
    if (error || !data?.signedUrl) {
      return null;
    }
    
    return data.signedUrl;
  } catch (error) {
    console.error("Error getting packing slip:", error);
    return null;
  }
}; 